import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  LinearProgress,
  Divider,
  Checkbox,
  CircularProgress,
  Card,
  CardContent,
  AlertTitle,
  Paper,
} from '@mui/material';
import {
  Close,
  Warning,
  AccountBalance,
  CreditCard,
  AttachMoney,
  Error as ErrorIcon,
  InfoOutlined,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getApplicantDisplayName } from './castingCallHelpers';
import client from '../../../../../API';
import { replaceConsoleError } from '../../../../../Utils/toastNotifications';

const PayoutDialog = ({ 
  open, 
  onClose, 
  applicant, 
  castingCall,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  
  // Form state
  const [payoutAmount, setPayoutAmount] = useState('');
  const [fundingSource, setFundingSource] = useState('balance'); // Default to balance
  const [role, setRole] = useState('Creator');
  const [notes, setNotes] = useState('');
  const [autoApprove, setAutoApprove] = useState(false);
  const [error, setError] = useState(null);

  // Budget validation state
  const [budgetValidation, setBudgetValidation] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  // Check for existing pending payout
  const { data: existingPayoutCheck, isLoading: checkingExisting } = useQuery(
    ['existingPayout', castingCall?.id, applicant?.username || applicant?.id],
    () => client.castingCalls.checkExistingPayout(castingCall.id, {
      recipient_type: castingCall.call_type === 'creator' ? 'creator' : 'user',
      recipient_id: castingCall.call_type === 'creator' ? applicant.username : applicant.id,
    }),
    {
      enabled: open && !!castingCall?.id && !!applicant,
      refetchOnWindowFocus: false,
    }
  );

  // Load budget status
  const { data: budgetStatus, isLoading: loadingBudget } = useQuery(
    ['castingCallBudget', castingCall?.id],
    () => client.castingCalls.getBudgetStatus(castingCall.id),
    {
      enabled: open && !!castingCall?.id,
      refetchOnWindowFocus: false,
      select: (data) => data.budget_status,
    }
  );

  // Create payout mutation
  const { mutate: createPayout, isLoading: isCreating } = useMutation(
    (data) => client.castingCalls.createPayout(castingCall.id, data),
    {
      onSuccess: (response) => {
        if (response.funding_error || response.error_type === 'funding_failed') {
          setError(
            `Payout created but approval failed: ${response.funding_error || response.error}. ` +
            `You can approve it manually later.`
          );
        } else {
          queryClient.invalidateQueries(['castingCall', castingCall.id]);
          queryClient.invalidateQueries(['castingCallBudget', castingCall.id]);
          queryClient.invalidateQueries(['castingCallPayouts', castingCall.id]);
          
          if (onSuccess) {
            onSuccess(response.payout);
          }
          handleClose();
        }
      },
      onError: (err) => {
        replaceConsoleError('Failed to create payout', err);
        const errorData = err.response?.data;
        setError(errorData?.error || 'Failed to create payout');
        
        if (errorData?.error_type === 'duplicate') {
          queryClient.invalidateQueries(['existingPayout']);
        }
      },
    }
  );

  // Validate budget when amount changes
  useEffect(() => {
    const amount = parseFloat(payoutAmount);
    if (amount > 0 && open) {
      validateBudget(amount);
    } else {
      setBudgetValidation(null);
    }
  }, [payoutAmount, open]);

  // Pre-fill amount if applicant has assigned payout
  useEffect(() => {
    if (open && applicant?.assigned_payout) {
      setPayoutAmount(applicant.assigned_payout.toString());
    }
  }, [open, applicant]);

  // Set smart default funding source
  useEffect(() => {
    if (budgetValidation) {
      // Default to balance if available, otherwise credit
      if (parseFloat(budgetValidation.available_balance) >= parseFloat(payoutAmount || 0)) {
        setFundingSource('balance');
      } else if (parseFloat(budgetValidation.available_credit) > 0) {
        setFundingSource('credit_line');
      }
    }
  }, [budgetValidation, payoutAmount]);

  // Validate budget
  const validateBudget = async (amount) => {
    if (!amount || amount <= 0) return;
    
    setIsValidating(true);
    try {
      const response = await client.castingCalls.validateBudget(castingCall.id, amount);
      setBudgetValidation(response.validation);
    } catch (err) {
      replaceConsoleError('Budget validation failed', err);
      setBudgetValidation(null);
    } finally {
      setIsValidating(false);
    }
  };

  // Calculate net amount after fees
  const netAmount = useMemo(() => {
    const amount = parseFloat(payoutAmount) || 0;
    if (role === 'Creator' && fundingSource === 'credit_line') {
      return amount * 0.95; // 5% fee for credit line
    }
    return amount;
  }, [payoutAmount, role, fundingSource]);

  const fee = parseFloat(payoutAmount) - netAmount;

  // Can fund from selected source
  const canFundFromSource = useMemo(() => {
    if (!budgetValidation || !payoutAmount) return false;
    const amount = parseFloat(payoutAmount);
    
    if (fundingSource === 'balance') {
      return parseFloat(budgetValidation.available_balance) >= amount;
    } else if (fundingSource === 'credit_line') {
      return parseFloat(budgetValidation.available_credit) >= amount;
    }
    return false;
  }, [budgetValidation, payoutAmount, fundingSource]);

  // Validation
  const validationError = useMemo(() => {
    const amount = parseFloat(payoutAmount);

    if (!amount || amount <= 0) {
      return { message: 'Please enter a valid payout amount', severity: 'error' };
    }

    if (budgetStatus && amount > parseFloat(budgetStatus.remaining)) {
      return { 
        message: `Amount exceeds remaining budget ($${budgetStatus.remaining})`, 
        severity: 'error' 
      };
    }

    if (existingPayoutCheck?.has_pending) {
      return {
        message: `A pending payout already exists. Please approve or cancel it first.`,
        severity: 'error'
      };
    }

    if (budgetValidation && !canFundFromSource && !isValidating) {
      return {
        message: `Insufficient ${fundingSource === 'balance' ? 'balance' : 'credit'}. Switch funding source or add funds.`,
        severity: 'error'
      };
    }

    return null;
  }, [payoutAmount, budgetStatus, budgetValidation, canFundFromSource, isValidating, existingPayoutCheck, fundingSource]);

  // Handle form submission
  const handleSubmit = () => {
    setError(null);
    
    if (validationError) {
      setError(validationError.message);
      return;
    }

    const recipientType = castingCall.call_type === 'creator' ? 'creator' : 'user';
    const recipientId = castingCall.call_type === 'creator' ? applicant.username : applicant.id;

    createPayout({
      recipient_type: recipientType,
      recipient_id: recipientId,
      amount: parseFloat(payoutAmount),
      funding_source: fundingSource,
      notes: notes.trim(),
      role: role,
      auto_approve: autoApprove,
    });
  };

  const handleClose = () => {
    setPayoutAmount('');
    setFundingSource('balance');
    setRole('Creator');
    setNotes('');
    setAutoApprove(false);
    setError(null);
    setBudgetValidation(null);
    onClose();
  };

  const canSubmit = !validationError && !isCreating && !isValidating && !checkingExisting && payoutAmount;

  if (!applicant || !castingCall) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <AttachMoney color="primary" />
            <Typography variant="h6">Create Payout</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Recipient Info */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Recipient
            </Typography>
            <Typography variant="h6">
              {getApplicantDisplayName(applicant, castingCall)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {castingCall.call_type === 'creator' ? 'Creator' : 'Worker'} • {applicant.email || applicant.username}
            </Typography>
          </Box>

          <Divider />

          {/* Payout Amount */}
          <TextField
            fullWidth
            label="Payout Amount"
            type="number"
            value={payoutAmount}
            onChange={(e) => setPayoutAmount(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            disabled={isCreating || existingPayoutCheck?.has_pending}
            autoFocus
            error={!!validationError}
          />

          {/* Budget Status - Compact */}
          {budgetStatus && (
            <Box>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Casting Call Budget
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ${parseFloat(budgetStatus.remaining).toFixed(2)} / ${parseFloat(budgetStatus.budget).toFixed(2)} remaining
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(
                  (parseFloat(budgetStatus.allocated) / parseFloat(budgetStatus.budget)) * 100,
                  100
                )}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'action.hover',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: parseFloat(budgetStatus.remaining) < parseFloat(budgetStatus.budget) * 0.2
                      ? 'error.main'
                      : 'success.main',
                  },
                }}
              />
            </Box>
          )}

          {/* Available Funds - Simple Card */}
          {isValidating ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : budgetValidation && (
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <InfoOutlined fontSize="small" color="primary" />
                Available Funds
              </Typography>
              
              <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1}>
                    <AccountBalance fontSize="small" color="primary" />
                    <Typography variant="body2">Personal Balance</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600}>
                    ${parseFloat(budgetValidation.available_balance).toFixed(2)}
                  </Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1}>
                    <CreditCard fontSize="small" color="info" />
                    <Typography variant="body2">Spending Power (5% fee)</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600}>
                    ${parseFloat(budgetValidation.available_credit).toFixed(2)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          )}

          {/* Funding Source Selection */}
          {budgetValidation && payoutAmount && (
            <FormControl component="fieldset">
              <FormLabel component="legend">Payment Source</FormLabel>
              <RadioGroup
                value={fundingSource}
                onChange={(e) => setFundingSource(e.target.value)}
              >
                <FormControlLabel
                  value="balance"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">
                        Personal Balance {parseFloat(budgetValidation.available_balance) < parseFloat(payoutAmount) && '(Insufficient)'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        No fees • Instant
                      </Typography>
                    </Box>
                  }
                  disabled={isCreating || parseFloat(budgetValidation.available_balance) < parseFloat(payoutAmount)}
                />
                <FormControlLabel
                  value="credit_line"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">
                        Company Spending Power {parseFloat(budgetValidation.available_credit) < parseFloat(payoutAmount) && '(Insufficient)'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        5% processing fee • {role === 'Creator' ? 'Deducted from creator' : 'You pay separately'}
                      </Typography>
                    </Box>
                  }
                  disabled={isCreating || parseFloat(budgetValidation.available_credit) < parseFloat(payoutAmount)}
                />
              </RadioGroup>
            </FormControl>
          )}

          {/* Fee Payer - Simple */}
          {fundingSource === 'credit_line' && (
            <FormControl component="fieldset">
              <FormLabel component="legend">Who pays the 5% processing fee?</FormLabel>
              <RadioGroup
                row
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <FormControlLabel 
                  value="Creator" 
                  control={<Radio />} 
                  label="Creator (deducted from payout)" 
                  disabled={isCreating}
                />
                <FormControlLabel 
                  value="Client" 
                  control={<Radio />} 
                  label="You (separate charge)" 
                  disabled={isCreating}
                />
              </RadioGroup>
            </FormControl>
          )}

          {/* Payout Summary */}
          {payoutAmount && parseFloat(payoutAmount) > 0 && (
            <Alert severity="info" icon={<AttachMoney />}>
              <Typography variant="body2">
                Creator receives: <strong>${netAmount.toFixed(2)}</strong>
              </Typography>
              {fee > 0 && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Processing fee: ${fee.toFixed(2)} ({role === 'Creator' ? 'deducted from creator' : 'charged to you'})
                </Typography>
              )}
              {fundingSource === 'balance' && (
                <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
                  ✓ No processing fees
                </Typography>
              )}
            </Alert>
          )}

          {/* Validation Error */}
          {validationError && (
            <Alert severity={validationError.severity} icon={<ErrorIcon />}>
              {validationError.message}
            </Alert>
          )}

          {/* Notes */}
          <TextField
            fullWidth
            label="Notes (optional)"
            multiline
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isCreating}
            placeholder="Add any notes about this payout..."
          />

          {/* Auto-Approve */}
          <FormControlLabel
            control={
              <Checkbox
                checked={autoApprove}
                onChange={(e) => setAutoApprove(e.target.checked)}
                disabled={isCreating || !canFundFromSource}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Approve and fund immediately</Typography>
                <Typography variant="caption" color="text.secondary">
                  {!canFundFromSource && payoutAmount 
                    ? 'Insufficient funds for auto-approval'
                    : 'Payout will be processed right away'}
                </Typography>
              </Box>
            }
          />

          {/* Error Display */}
          {error && (
            <Alert 
              severity="error"
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={isCreating && <CircularProgress size={16} />}
        >
          {isCreating 
            ? 'Creating...' 
            : autoApprove 
            ? 'Create & Approve' 
            : 'Create Payout'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PayoutDialog;